import fetch from 'isomorphic-fetch'
import queryString from 'query-string'
import * as R from 'ramda'

import { stringify } from 'utils/json'
import { getCookie } from './cookie'

const CROSS_ORIGIN = R.pathEq(['location', 'port'], '3000', global)

// default options for fetch request
// https://developer.mozilla.org/en-US/docs/Web/RestApi/Fetch_API/Using_Fetch#Supplying_request_options
const FETCH_BASE_REQUEST_OPTIONS = {
  method: 'GET',
  // Cross-Origin Resource Sharing (since it's a different port)
  // if serving the RestApi and the app on the same domain and port,
  // use 'same-origin' instead
  // https://developer.mozilla.org/en-US/docs/Web/RestApi/Request/mode
  mode: CROSS_ORIGIN ? 'cors' : 'same-origin',
  // https://developer.mozilla.org/en-US/docs/Web/RestApi/Request/cache
  cache: 'no-store',
  // https://developer.mozilla.org/en-US/docs/Web/RestApi/Request/credentials
  credentials: 'same-origin',
  redirect: 'error',
}

// Calculate difference in milliseconds between server and client clock
// getServerTime :: Response -> {serverTime: string, getServerTime: number}
export const getServerTime = response => {
  const serverTime = new Date(response.headers.get('date'))
  return {
    serverTime: serverTime.toISOString(),
    timeDelta: Math.trunc((serverTime.getTime() - Date.now()) / 1000),
  }
}

// Perform fetch return promise containing an object with either an
// `error` or `response` property. {error: Object} | {response: Object}
// Usage in redux saga generator
// const { response, error } = yield call(apiFetch, 'url')
//
// apiFetch :: (String, Object) -> Promise[{error|response}]
const apiFetch = (url, options = {}) => {
  return fetch(url, options)
    .then(response =>
      response
        .json()
        .catch(() => Promise.resolve({}))
        .then(R.when(R.is(Array), R.objOf('results')))
        .then(data => (response.ok ? { response: data } : { error: data }))
        .then(
          R.assoc('meta', {
            url,
            HTTPstatus: response.status,
            ...getServerTime(response),
          }),
        ),
    )
    .catch(error => ({
      // fetch error – probably did not even get a http response from server.
      error: {
        message: error.message,
        url,
        options,
        stack: R.pipe(
          R.propOr('', 'stack'),
          R.split(/\n+/),
        )(error),
      },
    }))
}

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']

type ApiPayload = {
  formData?: FormData
} & Record<string, unknown>

// convenience wrapper for django-rest-framework RestApi methods
// returns an object containing promise returning functions for each http
// method. The `id` data value is always used for url routing.
// See apiFetch function for further details
//
// example usage:
// RestApi('https://example.com/RestApi').get('gizmos', {id: 42})
//
// RestApi :: (string, object?) -> {string: (string, {data}, string?) -> Promise}
export const RestApi = (urlRoot = '/api', extra_options = {}) =>
  R.pipe(
    R.map(method => [
      R.toLower(method),
      (route: string | string[], payload: ApiPayload = {}) => {
        const { formData, ...data } = payload
        const url = R.pipe(
          R.unless(R.is(Array), R.of),
          R.prepend(urlRoot),
          R.filter(R.identity),
          R.append(''),
          R.join('/'),
          url =>
            ['DELETE', 'GET'].includes(method)
              ? `${url}?${queryString.stringify(data, {
                  arrayFormat: 'comma',
                })}`
              : url,
        )(route)
        const body = R.test(/^P/, method) ? formData || stringify(data) : null
        const headers: Record<string, string> = {
          'X-CSRFToken': getCookie('csrftoken', 'NO TOKEN'),
        }
        if (!formData) headers['Content-Type'] = 'application/json'
        const options = R.reduce(R.mergeDeepRight, {}, [
          FETCH_BASE_REQUEST_OPTIONS,
          extra_options,
          { method, body, headers },
        ])
        return apiFetch(url, options)
      },
    ]),
    R.fromPairs,
  )(HTTP_METHODS)

const API_URL = CROSS_ORIGIN
  ? 'http://localhost:8000/api' // cross origin in development
  : '/api' // same-origin

export default RestApi(API_URL)
